// 🏰 THE POG KINGDOM — pull DOWN from the map and you're INSIDE the kingdom skies:
// balloons drifting, the POG EXPRESS rolling, the Sky Fleet flying overhead.
// Pull UP from the bottom and you're on PICS · FLICS · LIVE.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Flame, Radio } from "lucide-react";
import { BALLOON_FLEET } from "@/lib/mapConstants";
import { VEHICLE_ART, VEHICLE_PAINT } from "@/components/SkyVehicles";
import DealsBoardSheet from "@/components/DealsBoardSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const swipeHandlers = (dir, onTrigger) => {
  let startY = null;
  return {
    onTouchStart: (e) => { startY = e.touches[0].clientY; },
    onTouchMove: (e) => {
      if (startY == null) return;
      const dy = e.touches[0].clientY - startY;
      if ((dir === "down" && dy > 46) || (dir === "up" && dy < -46)) { startY = null; onTrigger(); }
    },
    onTouchEnd: () => { startY = null; },
    onClick: onTrigger,
  };
};

export const KingdomTab = ({ onOpen }) => (
  <button
    data-testid="kingdom-pull-tab"
    {...swipeHandlers("down", onOpen)}
    className="absolute left-1/2 -translate-x-1/2 top-[max(0.75rem,env(safe-area-inset-top))] z-[1050] px-4 pt-1 pb-1.5 rounded-2xl bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-md flex flex-col items-center gap-0 hover:bg-slate-600/90 transition-colors"
    title="Pull down — THE DEALS BOARD"
  >
    <span className="text-[9px] font-black tracking-widest text-slate-100">📋 DEALS BOARD</span>
    <span className="text-slate-100 text-[10px] leading-none animate-bounce">⌄</span>
  </button>
);

export const BeamTab = ({ onOpen }) => (
  <button
    data-testid="beam-pull-tab"
    {...swipeHandlers("up", onOpen)}
    className="absolute left-1/2 -translate-x-1/2 bottom-9 z-[1050] px-4 pb-0.5 pt-1.5 rounded-t-2xl bg-slate-700/90 backdrop-blur-md border border-b-0 border-white/20 shadow-md flex flex-col items-center hover:bg-slate-600/90 transition-colors"
    title="Pull up — PICS · FLICS · LIVE"
  >
    <span className="text-slate-100 text-[10px] leading-none animate-bounce">⌃</span>
    <span className="text-[9px] font-black tracking-widest text-slate-100 flex items-center gap-1"><Flame className="w-2.5 h-2.5" /> PICS·FLICS·LIVE</span>
  </button>
);

const FLYBYS = [
  { id: "blimp", w: 120, top: "12%", dur: 34, delay: 0, paint: "sky" },
  { id: "ufo", w: 62, top: "22%", dur: 22, delay: 6, paint: "violet" },
  { id: "hot_air", w: 66, top: "30%", dur: 40, delay: 2, paint: null },
  { id: "rocket", w: 48, top: "8%", dur: 14, delay: 12, paint: "crimson" },
  { id: "drone", w: 52, top: "38%", dur: 26, delay: 9, paint: "emerald" },
  { id: "plane", w: 86, top: "17%", dur: 28, delay: 16, paint: "gold" },
];

const STARS = Array.from({ length: 26 }, (_, i) => ({
  left: `${(i * 37) % 100}%`, top: `${(i * 23) % 55}%`, s: 1 + (i % 3), d: (i % 5) * 0.7,
}));

// 📋 The pull-down page is now THE DEALS BOARD (Sir Eric's 30-year GEM).
export default function KingdomSheet(props) {
  return <DealsBoardSheet {...props} />;
}

// eslint-disable-next-line no-unused-vars
function LegacyKingdomSheet({ open, onClose }) {
  const navigate = useNavigate();
  const startY = useRef(null);
  const [lives, setLives] = useState([]);
  const [tapped, setTapped] = useState({});
  const [zap, setZap] = useState(null);
  const tapCraft = async (ship) => {
    if (tapped[ship]) return;
    setTapped((t) => ({ ...t, [ship]: true }));
    try {
      const r = await axios.post(`${BACKEND_URL}/api/kingdom/tap`, { ship }, { withCredentials: true });
      setZap(r.data?.message || "+1 POG BUCK 🏰");
      setTimeout(() => setZap(null), 2200);
    } catch { /* silent */ }
  };
  useEffect(() => {
    if (!open) return;
    axios.get(`${BACKEND_URL}/api/live`).then((r) => setLives(r.data?.lives || [])).catch(() => {});
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);
  if (!open) return null;
  const go = (path) => { onClose(); navigate(path); };
  return (
    <div
      className="fixed inset-0 z-[1400] overflow-hidden bg-gradient-to-b from-[#0b0420] via-[#221046] to-[#3b1668] animate-in slide-in-from-top duration-300"
      data-testid="kingdom-sheet"
      onTouchStart={(e) => { startY.current = e.touches[0].clientY; }}
      onTouchMove={(e) => { if (startY.current != null && startY.current - e.touches[0].clientY > 70) { startY.current = null; onClose(); } }}
      onTouchEnd={() => { startY.current = null; }}
    >
      <style>{`
        @keyframes kg-cross { from { transform: translateX(-140px); } to { transform: translateX(calc(100vw + 160px)); } }
        @keyframes kg-bob { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-26px) rotate(2deg); } }
        @keyframes kg-train { from { transform: translateX(calc(100vw + 120px)); } to { transform: translateX(-360px); } }
        @keyframes kg-twinkle { 0%,100% { opacity: .25; } 50% { opacity: 1; } }
      `}</style>
      {/* stars */}
      {STARS.map((s, i) => (
        <span key={i} className="absolute rounded-full bg-white pointer-events-none"
          style={{ left: s.left, top: s.top, width: s.s, height: s.s, animation: `kg-twinkle ${2.4 + s.d}s ease-in-out ${s.d}s infinite` }} />
      ))}
      {/* 🛸 sky fleet flybys — tap 'em for POG BUCKS! */}
      {FLYBYS.map((f) => {
        const Art = VEHICLE_ART[f.id];
        return Art && !tapped[f.id] ? (
          <button key={f.id} data-testid={`kingdom-craft-${f.id}`} onClick={() => tapCraft(f.id)}
            className="absolute cursor-pointer bg-transparent border-0 p-0" style={{ top: f.top, left: 0, animation: `kg-cross ${f.dur}s linear ${f.delay}s infinite` }}>
            <Art w={f.w} tint={f.paint ? VEHICLE_PAINT[f.paint] : null} />
          </button>
        ) : null;
      })}
      {/* 🎈 balloon fest — tappable too */}
      {BALLOON_FLEET.map((b, i) => (
        !tapped[`balloon${i}`] && (
          <img key={i} src={b.img} alt="" data-testid={`kingdom-balloon-${i}`} onClick={() => tapCraft(`balloon${i}`)}
            className="absolute cursor-pointer drop-shadow-[0_0_18px_rgba(168,85,247,0.35)]"
            style={{ width: b.w, left: b.left, top: `${34 + (i % 4) * 11}%`, animation: `kg-bob ${b.dur}s ease-in-out ${b.delay}s infinite` }} />
        )
      ))}
      {zap && (
        <div data-testid="kingdom-zap" className="absolute top-[46%] left-1/2 -translate-x-1/2 z-20 rounded-full bg-amber-400 text-slate-900 font-heading font-black text-sm px-4 py-2 shadow-xl animate-bounce whitespace-nowrap">
          {zap}
        </div>
      )}
      {/* 🚂 the POG EXPRESS rolling the kingdom rails */}
      <div className="absolute bottom-[86px] left-0 right-0 pointer-events-none">
        <div className="flex items-end gap-1" style={{ animation: "kg-train 18s linear 0s infinite", width: "max-content" }}>
          <span className="text-3xl">🚃</span><span className="text-3xl">🚃</span><span className="text-3xl">🚃</span>
          <img src="/pog-express-engine.png" alt="POG EXPRESS" className="h-14 w-auto drop-shadow-[0_0_14px_rgba(16,185,129,0.5)]" onError={(e) => { e.currentTarget.outerHTML = "<span style='font-size:34px'>🚂</span>"; }} />
        </div>
        <div className="h-1 bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      </div>
      {/* header + actions */}
      <div className="relative z-10 px-5 pt-5 max-w-lg mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-black text-2xl text-white tracking-wide drop-shadow">🏰 THE POG KINGDOM</h2>
            <p className="text-[12px] text-violet-300 font-bold">Balloons · Trains · the Sky Fleet — the skies above your map.</p>
          </div>
          <button data-testid="kingdom-close" onClick={onClose} className="w-10 h-10 rounded-xl bg-slate-900/70 border border-violet-500/40 hover:bg-slate-800 flex items-center justify-center shrink-0">
            <X className="w-5 h-5 text-slate-200" />
          </button>
        </div>
      </div>
      <div className="absolute bottom-4 left-0 right-0 z-10 px-5 max-w-lg mx-auto">
        {lives.length > 0 && (
          <div className="mb-2" data-testid="kingdom-live-rail">
            <p className="text-[10px] font-black tracking-widest text-rose-300 mb-1 flex items-center gap-1">
              <Radio className="w-3 h-3 animate-pulse" /> LIVE ON THE POG BEAM
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {lives.map((l) => (
                <button key={l.id} data-testid={`kingdom-live-${l.id}`} onClick={() => go(`/live/${l.id}`)}
                  className="shrink-0 w-44 rounded-2xl border border-rose-400/50 bg-rose-500/15 backdrop-blur px-3 py-2 text-left hover:bg-rose-500/25 transition-colors">
                  <span className="flex items-center gap-1.5 text-[9px] font-black text-rose-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" /> LIVE {l.baron ? "· 👑 BARON" : ""}
                  </span>
                  <span className="block font-heading font-extrabold text-[12px] text-white truncate">{l.title || "POG BEAM"}</span>
                  <span className="block text-[10px] text-rose-200 truncate">{l.owner_name || l.city || "Tap to watch"}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2">
        <button data-testid="kingdom-fleet" onClick={() => go("/fleet")}
          className="flex-1 rounded-2xl border border-cyan-400/50 bg-cyan-500/15 backdrop-blur px-3 py-2.5 text-left hover:bg-cyan-500/25 transition-colors">
          <span className="block font-heading font-black text-sm text-white">🛸 FLY THE FLEET</span>
          <span className="block text-[10px] text-cyan-200">Put a business in the sky</span>
        </button>
        <button data-testid="kingdom-legend" onClick={() => go("/legend")}
          className="flex-1 rounded-2xl border border-amber-400/50 bg-amber-500/15 backdrop-blur px-3 py-2.5 text-left hover:bg-amber-500/25 transition-colors">
          <span className="block font-heading font-black text-sm text-white">📖 THE LEGEND</span>
          <span className="block text-[10px] text-amber-200">How the Kingdom rose</span>
        </button>
        </div>
      </div>
      <p className="absolute bottom-[68px] left-1/2 -translate-x-1/2 z-10 text-[10px] text-violet-300/80 font-bold tracking-widest">SWIPE UP TO RETURN TO THE MAP ⌃</p>
    </div>
  );
}
